import React from 'react';
import type {
  GoalProgress,
  GoalPeriodGroup,
  GoalSelectionCategory,
  GoalsOverviewLabels,
} from '../../shared/types/goals';

interface GoalCardProps {
  goal: GoalProgress;
  labels: GoalsOverviewLabels;
}

function GoalCard({ goal, labels }: GoalCardProps) {
  return (
    <div className="col-12 col-md-6 col-lg-4">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h6 className="mb-0 fw-bold">{goal.fieldName}</h6>
            <span className={`badge ${goal.achieved ? 'bg-success' : 'bg-secondary'}`}>
              {goal.actualCount}/{goal.targetCount}
            </span>
          </div>

          <div className="progress mb-2" style={{ height: 10 }}>
            <div
              className={`progress-bar ${goal.achieved ? 'bg-success' : 'bg-primary'}`}
              role="progressbar"
              style={{ width: `${goal.completionPercentage}%` }}
              aria-valuenow={goal.completionPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            ></div>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {goal.completionPercentage}% {labels.complete}
            </small>
            {goal.achieved && (
              <span className="badge bg-success">
                <i className="bi bi-check-circle-fill"></i> {labels.achieved}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface PeriodGroupProps {
  periodGroup: GoalPeriodGroup;
  weekOffset: number;
  canNavigateToPreviousWeek: boolean;
  canNavigateToNextWeek: boolean;
  onWeekOffsetChange: (offset: number) => void;
  labels: GoalsOverviewLabels;
}

function PeriodGroup({
  periodGroup,
  weekOffset,
  canNavigateToPreviousWeek,
  canNavigateToNextWeek,
  onWeekOffsetChange,
  labels,
}: PeriodGroupProps) {
  const isWeek = periodGroup.periodType === 'week';

  return (
    <div className="card mb-4 shadow-sm">
      <div className="card-header text-white" style={{ background: 'var(--traq-gradient-primary)' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-calendar-range me-2"></i>
              {periodGroup.periodLabel}
            </h5>
          </div>

          {isWeek && (
            <div className="btn-group" role="group" data-testid="week_navigation">
              {canNavigateToPreviousWeek && (
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm"
                  onClick={() => onWeekOffsetChange(weekOffset - 1)}
                  data-testid="previous_week_button"
                >
                  <i className="bi bi-chevron-left"></i>
                  <span className="d-none d-md-inline ms-1">{labels.previousWeek}</span>
                </button>
              )}
              {canNavigateToNextWeek && (
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm"
                  onClick={() => onWeekOffsetChange(weekOffset + 1)}
                  data-testid="next_week_button"
                >
                  <span className="d-none d-md-inline me-1">{labels.nextWeek}</span>
                  <i className="bi bi-chevron-right"></i>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card-body">
        <div className="row g-3">
          {periodGroup.goals.map((goal, index) => (
            <GoalCard key={`${goal.fieldName}-${index}`} goal={goal} labels={labels} />
          ))}
        </div>
      </div>
    </div>
  );
}

interface CategorySelectionProps {
  categories: GoalSelectionCategory[];
  onSelectField: (categoryId: string, itemId: string, type: 'toggle_field' | 'field_set') => void;
  labels: GoalsOverviewLabels;
}

function CategorySelection({ categories, onSelectField, labels }: CategorySelectionProps) {
  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">
          <i className="bi bi-folder me-2"></i>
          {labels.selectCategory}
        </h5>
      </div>
      <div className="card-body">
        {categories.map((category) => (
          <div key={category.id} className="mb-4">
            <h6 className="fw-bold mb-3">{category.name}</h6>
            <div className="list-group">
              {category.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  onClick={() => onSelectField(category.id, item.id, item.type)}
                  disabled={item.hasGoal}
                >
                  <span>
                    {item.icon && (
                      <img src={item.icon} alt="" className="me-2" style={{ width: 24, height: 24 }} />
                    )}
                    {item.name}
                    {item.type === 'field_set' && (
                      <span className="badge bg-secondary ms-2">
                        {item.fields?.length || 0} fields
                      </span>
                    )}
                  </span>
                  {item.hasGoal && (
                    <span className="badge bg-success">
                      <i className="bi bi-check-circle"></i> {labels.hasGoal}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface GoalsOverviewPageProps {
  periodGroups: GoalPeriodGroup[];
  weekOffset: number;
  canNavigateToPreviousWeek: boolean;
  canNavigateToNextWeek: boolean;
  categories: GoalSelectionCategory[];
  onWeekOffsetChange: (offset: number) => void;
  onCreateGoalClick: () => void;
  onSelectField: (categoryId: string, itemId: string, type: 'toggle_field' | 'field_set') => void;
  labels: GoalsOverviewLabels;
  achievementsUrl: string;
  isLoading?: boolean;
  view: 'overview' | 'select_category';
  onBackToOverview: () => void;
}

export function GoalsOverviewPage({
  periodGroups,
  weekOffset,
  canNavigateToPreviousWeek,
  canNavigateToNextWeek,
  categories,
  onWeekOffsetChange,
  onCreateGoalClick,
  onSelectField,
  labels,
  achievementsUrl,
  isLoading = false,
  view,
  onBackToOverview,
}: GoalsOverviewPageProps) {
  const hasAnyGoals = periodGroups.some((pg) => pg.goals.length > 0);

  if (isLoading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'select_category') {
    return (
      <div className="container mt-4">
        <div className="row">
          <div className="col-12">
            <div className="mb-4">
              <button type="button" className="btn btn-outline-secondary" onClick={onBackToOverview}>
                <i className="bi bi-arrow-left me-1"></i>
                {labels.back}
              </button>
            </div>
            <CategorySelection
              categories={categories}
              onSelectField={onSelectField}
              labels={labels}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="mb-0">
              <i className="bi bi-bullseye me-2"></i>
              {labels.title}
            </h1>
            <div className="btn-group" role="group">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onCreateGoalClick}
                data-testid="create_new_goal_button"
              >
                <i className="bi bi-plus-circle"></i>
                <span className="d-none d-sm-inline ms-1">{labels.createNewGoal}</span>
              </button>
              <a href={achievementsUrl} className="btn btn-outline-primary">
                <i className="bi bi-trophy"></i>
                <span className="d-none d-sm-inline ms-1">{labels.achievements}</span>
              </a>
            </div>
          </div>

          {!hasAnyGoals ? (
            <div className="alert alert-info text-center">
              <i className="bi bi-info-circle me-2"></i>
              {labels.noGoals}
            </div>
          ) : (
            periodGroups
              .filter((pg) => pg.goals.length > 0)
              .map((periodGroup) => (
                <PeriodGroup
                  key={periodGroup.periodType}
                  periodGroup={periodGroup}
                  weekOffset={weekOffset}
                  canNavigateToPreviousWeek={canNavigateToPreviousWeek}
                  canNavigateToNextWeek={canNavigateToNextWeek}
                  onWeekOffsetChange={onWeekOffsetChange}
                  labels={labels}
                />
              ))
          )}
        </div>
      </div>
    </div>
  );
}

export default GoalsOverviewPage;
